import React from 'react';
import { Box, Container, Divider, Link } from '@mui/material';
import Typography from '../components/i18n/LocalizedTypography';

const WEBSITE_NAME = 'Kundlistar Studio';
const WEBSITE_URL = 'https://kundlistarstudio.com';
const CONTACT_EMAIL = 'info@kundlistarstudio.com';
const CONTACT_PHONE = '+91 8284927820';
const CONTACT_ADDRESS = 'India';

const TermsOfService: React.FC = () => {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Updated: 07 July 2026
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            These Terms and Conditions ("Terms") govern your access to and use of {WEBSITE_NAME},
            available at <Link href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">{WEBSITE_URL}</Link>,
            and any products, services, content, features, or tools provided through the website.
          </Typography>
        </Box>

        <Divider sx={{ mb: 5 }} />

        <Section title="1. Definitions">
          <BulletList items={[
            `"Website" means ${WEBSITE_URL}.`,
            `"Company", "we", "us", or "our" means ${WEBSITE_NAME}.`,
            '"User", "you", or "your" means any person who accesses or uses the website.',
            '"Services" means the products, services, information, features, or functionality offered through the website.',
          ]} />
        </Section>

        <Section title="2. Use of the Website">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            You agree to use the website only for lawful purposes and in accordance with these Terms.
            You must not:
          </Typography>
          <BulletList items={[
            'Use the website for any illegal, fraudulent, or harmful purpose.',
            'Attempt to gain unauthorized access to the website, servers, systems, or user accounts.',
            'Copy, scrape, reproduce, or exploit website content without permission.',
            'Upload or transmit viruses, malware, spam, or harmful code.',
            'Interfere with the security, performance, or proper working of the website.',
            'Misrepresent your identity or provide false information.',
            'Use the website in a way that violates any applicable law or regulation.',
          ]} />
        </Section>

        <Section title="3. Account Registration">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            Some features may require you to create an account or provide certain information. You
            agree to:
          </Typography>
          <BulletList items={[
            'Provide accurate and complete information.',
            'Keep your login details confidential.',
            'Update your information when necessary.',
            'Accept responsibility for all activity under your account.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            We may suspend or terminate your account if we believe you have violated these Terms or
            used the website improperly.
          </Typography>
        </Section>

        <Section title="4. Products and Services">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            We make reasonable efforts to describe our products or services accurately. However, we do
            not guarantee that descriptions, pricing, availability, images, or other content on the
            website will always be complete, accurate, current, or error-free. We reserve the right to:
          </Typography>
          <BulletList items={[
            'Modify or discontinue any product or service.',
            'Refuse or cancel orders or service requests.',
            'Correct errors in pricing or descriptions.',
            'Limit quantities or availability.',
            'Change features, specifications, or content at any time.',
          ]} />
        </Section>

        <Section title="5. Pricing and Payments">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            All prices are listed in INR unless stated otherwise. By making a payment, you agree that:
          </Typography>
          <BulletList items={[
            'You are authorized to use the selected payment method.',
            'The payment information provided is accurate.',
            'Applicable taxes, fees, or charges may be added.',
            'Payment processing may be handled by third-party payment providers.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            We are not responsible for payment failures, bank charges, gateway errors, or delays
            caused by third-party payment processors.
          </Typography>
        </Section>

        <Section title="6. Cancellation, Refunds, and Returns">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            For services, once a service has started or been delivered, refunds may not be available
            unless specifically agreed in writing. Digital products, downloadable files, templates,
            software, reports, or online resources are generally non-refundable once accessed,
            downloaded, generated, or delivered.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Refunds, if approved, will be processed to the original payment method within 7 business
            days. For refund or cancellation requests, contact us at{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
          </Typography>
        </Section>

        <Section title="7. Intellectual Property">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            All content on the website, including text, graphics, logos, icons, images, videos,
            designs, software, layout, trademarks, service marks, and other materials, is owned by or
            licensed to {WEBSITE_NAME}. You may not copy, reproduce, modify, distribute, display,
            sell, license, or exploit any website content without our prior written permission. You
            may use the website only for personal or internal business purposes unless we expressly
            permit otherwise.
          </Typography>
        </Section>

        <Section title="8. User Content">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            If you submit, upload, post, or share any content with us, including reviews, comments,
            testimonials, images, files, or feedback, you confirm that:
          </Typography>
          <BulletList items={[
            'You own or have permission to share such content.',
            'The content does not violate any law or third-party rights.',
            'The content is not defamatory, abusive, obscene, misleading, or harmful.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            By submitting content, you grant us a non-exclusive, worldwide, royalty-free license to
            use, reproduce, display, publish, and modify such content for business, marketing, service
            improvement, or operational purposes. We reserve the right to remove user content at our
            discretion.
          </Typography>
        </Section>

        <Section title="9. Third-Party Links and Services">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Our website may contain links to third-party websites, tools, payment gateways, plugins,
            or services. We do not control and are not responsible for third-party websites or
            services, including their content, privacy practices, availability, or security. Your use
            of third-party services is subject to their own terms and policies.
          </Typography>
        </Section>

        <Section title="10. No Professional Advice">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            The information provided on our website is for general informational, educational,
            spiritual, and entertainment purposes only. Unless expressly stated, website content should
            not be considered legal, financial, medical, technical, or professional advice. You should
            seek professional advice before relying on any information for important decisions.
          </Typography>
        </Section>

        <Section title="11. Disclaimer of Warranties">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            The website and services are provided on an "as is" and "as available" basis. We do not
            guarantee that:
          </Typography>
          <BulletList items={[
            'The website will always be available or uninterrupted.',
            'The website will be free from errors, viruses, or harmful components.',
            'The information on the website will always be accurate or current.',
            'The services will meet your specific expectations or requirements.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            To the maximum extent permitted by law, we disclaim all warranties, express or implied.
          </Typography>
        </Section>

        <Section title="12. Limitation of Liability">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            To the maximum extent permitted by applicable law, {WEBSITE_NAME}, its owners, employees,
            partners, contractors, or affiliates will not be liable for any indirect, incidental,
            special, consequential, punitive, or exemplary damages arising from your use of the
            website or services. Our total liability for any claim related to the website or services
            will not exceed the amount paid by you to us for the specific product or service giving
            rise to the claim, or INR 1,000, whichever is lower.
          </Typography>
        </Section>

        <Section title="13. Indemnification">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            You agree to indemnify and hold harmless {WEBSITE_NAME}, its owners, employees,
            affiliates, partners, and service providers from any claims, losses, damages, liabilities,
            costs, or expenses arising from:
          </Typography>
          <BulletList items={[
            'Your use or misuse of the website.',
            'Your violation of these Terms.',
            'Your violation of any law or third-party rights.',
            'Any content or information you submit to us.',
          ]} />
        </Section>

        <Section title="14. Termination">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            We may suspend or terminate your access to the website or services at any time if:
          </Typography>
          <BulletList items={[
            'You violate these Terms.',
            'You misuse the website or services.',
            'We are required to do so by law.',
            'We discontinue the website or services.',
            'Your use may harm us, other users, or third parties.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            Termination does not affect any rights or obligations that existed before termination.
          </Typography>
        </Section>

        <Section title="15. Force Majeure">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We will not be responsible for any delay or failure to perform caused by events beyond our
            reasonable control, including natural disasters, internet failures, cyberattacks, strikes,
            war, government restrictions, power outages, technical failures, or other unforeseen
            events.
          </Typography>
        </Section>

        <Section title="16. Changes to These Terms">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We may update these Terms from time to time. Changes will be posted on this page with an
            updated "Last Updated" date. Your continued use of the website or services after changes
            are posted means you accept the updated Terms.
          </Typography>
        </Section>

        <Section title="17. Governing Law and Jurisdiction">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            These Terms will be governed by and interpreted in accordance with the laws of India. Any
            disputes arising from these Terms, the website, or our services will be subject to the
            exclusive jurisdiction of the courts located in Punjab, India.
          </Typography>
        </Section>

        <Section title="18. Third-Party Astronomy Data and Notices">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            Some astronomical calculations may use NASA NAIF SPICE Toolkit components and/or JPL
            planetary ephemeris kernels. SPICE is provided by NASA's Navigation and Ancillary
            Information Facility (NAIF).
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Use of NASA/JPL/NAIF materials does not imply endorsement by NASA, JPL, Caltech, or NAIF.
          </Typography>
        </Section>

        <Section title="19. Contact Us">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
            For questions about these Terms, contact us at:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1">{WEBSITE_NAME}</Typography>
            <Typography variant="body1">
              Email: <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
            </Typography>
            <Typography variant="body1">Phone: {CONTACT_PHONE}</Typography>
            <Typography variant="body1">Website: {WEBSITE_URL}</Typography>
            <Typography variant="body1">Address: {CONTACT_ADDRESS}</Typography>
          </Box>
        </Section>

        <Divider sx={{ my: 5 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          © {new Date().getFullYear()} {WEBSITE_NAME}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ pl: 0.5 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default TermsOfService;
